import { createContext, useContext, useMemo } from "react";
import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import { Filter } from "./WasteFacilityFilters";

export type WasteFacilityState = {
  optimisticFilters: Partial<Filter>;
  deferredFilters: Partial<Filter>;
  isPending: boolean;
};

type WasteFacilityContextType = {
  wasteFacilityState: WasteFacilityState;
  setWasteFacilityState: (cb: (state: Partial<Filter>) => void) => void;
};

const WasteFacilityStateContext = createContext<
  WasteFacilityContextType | undefined
>(undefined);

export function useWasteFacilityState() {
  const context = useContext(WasteFacilityStateContext);
  if (!context) {
    throw new Error(
      "useWasteFacilityState must be used within a WasteFacilityStateProvider"
    );
  }
  return context;
}

export const WasteFacilityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { optimisticFilters, deferredFilters, setFilters, isPending } =
    useSuspenseQueryParams();

  const wasteFacilityState = useMemo(
    () => ({ deferredFilters, optimisticFilters, isPending }),
    [deferredFilters, optimisticFilters, isPending]
  );

  const value: WasteFacilityContextType = useMemo(
    () => ({
      wasteFacilityState,
      setWasteFacilityState: setFilters,
    }),
    [wasteFacilityState, setFilters]
  );

  return (
    <WasteFacilityStateContext.Provider value={value}>
      {children}
    </WasteFacilityStateContext.Provider>
  );
};
